import IRequirementCreateService from 'features/requirements/services/IRequirementCreateService';
import PageCard from 'features/shared/components/card/PageCard';
import IContentDialogService from 'features/shared/components/contentDialog/IContentDialogService';
import { useForm } from 'features/shared/forms/hooks/useForm';
import { useInjection } from 'infrastructure/ioc/ioc.react';
import { IocTypes } from 'infrastructure/ioc/ioc.types';
import React, { useEffect, useState } from 'react';
import { reservedCapacityFormInitValidations, setCapacitiesValidation, validateMonthlyCapacityInput, validateAllCapacities, validateReduceOfYearlyCapacity } from './reservedCapacityFormValidation';
import ContractDropdownDTO from 'features/contracts/dtos/ContractDropdownDTO';
import ContractSelect from 'features/contracts/components/ContractSelect';
import { trackPromise } from 'react-promise-tracker';
import BasicInfoSubFormSection from 'features/requirements/sections/requirementFormTypes/basicInfoSubForm/BasicInfoSubFormSection';
import IReservedCapacityFormService from 'features/requirements/services/IReservedCapacityFormService';
import IRequirementFormService from 'features/requirements/services/IRequirementFormService';
import { ReservedCapacityFormSection_PageCard } from 'features/shared/components/loaders/promiseLoadingAreas';
import OptionType from 'features/shared/components/selectInput/OptionType';
import SelectInput from 'features/shared/components/selectInput/SelectInput';
import CapacityElectricityDTO from 'features/contracts/dtos/CapacityElectricityDTO';
import ReservedCapacitySubForm from 'features/requirements/components/reservedCapacityForm/ReservedCapacitySubForm';
import AnnualCapacity from 'features/requirements/components/reservedCapacityForm/AnnualCapacity';
import ReservedCapacityValidationSubDialog from 'features/requirements/components/reservedCapacityForm/ReservedCapacityValidationSubDialog';
import BaseRequirementFormProps from 'features/requirements/models/BaseRequirementFormProps';
import RenderWrapper from 'features/shared/components/renderWrapper/RenderWrapper';
import { openRequirementResultDialog } from 'features/requirements/helpers/requirementResultDialogHelper';
import PostResponse from 'data/PostResponse';
import { getContractDetailRootRoute } from 'features/shared/helpers/routeHelper';
import { RequirementResultDialogParams } from 'features/requirements/models/RequirementResultDialogParams';
import InsertRequirementResultDTO from 'features/requirements/dtos/InsertRequirementResultDTO';
import { useTranslation } from 'react-i18next';

const monthlyCapacityError = "Součet roční a měsíční kapacity nesmí přesáhnout rez. příkon.";

interface Props extends BaseRequirementFormProps {
  contracts: ContractDropdownDTO[];
  selectedYear?: number;
}

const ReservedCapacityFormSection = (props?: Props) => {
  //#region translations
  const tLabels = useTranslation("requirements\\requirements").t;
  //#endregion translations

  //#region Props
  const contracts = props?.contracts ? props.contracts : [];
  const useOutsideOfRequirements = props?.useOutsideOfRequirements ? true : false;
  const sendButtonTitle = props?.sendButtonTitle ? props.sendButtonTitle : tLabels("reservedCapacityFormSection.sendRequirement");
  const selectedYear = props?.selectedYear !== undefined ? props.selectedYear : null; 
  //#endregion Props

  //#region DI
  const createService = useInjection<IRequirementCreateService>(IocTypes.RequirementCreateService);
  const dialogService = useInjection<IContentDialogService>(IocTypes.ContentDialogService);
  const formService = useInjection<IReservedCapacityFormService>(IocTypes.ReservedCapacityFormService);
  const baseFormService = useInjection<IRequirementFormService>(IocTypes.RequirementFormService);
  //#endregion DI

  //#region state
  const [selectedContract, setSelectedContract] = useState<ContractDropdownDTO>({});
  const [years, setYears] = useState<OptionType[]>([]);
  const [months, setMonths] = useState<OptionType[]>([]);
  const [capacities, setCapacities] = useState<CapacityElectricityDTO[]>([]);
  //#endregion state

  //#region form hook
  const {
    values,
    setFormValue,
    errors,
    bindField,
    bindUncontrolledField,
    valideteForm,
    validateFormField,
    setValidationRule,
    setValidationError,
    clearValidationErrors
  } = useForm({
    initialValidations: reservedCapacityFormInitValidations(),
  });
  //#endregion

  //#region side-effects
  useEffect(() => {
    // set default contract
    if (contracts.length === 1) {
      contractChangeHandler(contracts[0]);
    }
  }, [contracts]);
  //#endregion

  //#region helpers
  const validateEntireForm = () => {
    let formValidation = valideteForm();
    let capaticiesValidation = capacities.length > 0;

    if (capaticiesValidation) {
      capaticiesValidation = validateAllCapacities(
        values,
        capacities,
        monthlyCapacityError,
        validateFormField,
        setValidationError
      );
    }

    return formValidation && capaticiesValidation;
  }
  
  const getSelectedYear = (years: OptionType[]): number => {
    let year = years[0].value;
    
    if (selectedYear !== null) {
      let yearOption = years.find(x => x.value === `${selectedYear}`);
      year = yearOption ? yearOption.value : year;
    }

    return Number(year);
  }
  //#endregion helpers

  //#region handlers
  const submitFormHandler = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateEntireForm()) {
      let isValid = await validateReduceOfYearlyCapacity(values, capacities, formService);
      if (isValid) {
        createRequirementHandler();
      } else {
        dialogService.openDialog(tLabels("reservedCapacityFormSection.warning"), () => (
          <ReservedCapacityValidationSubDialog
            createRequirementHandler={createRequirementHandler}
          />
        ));
      }
    }
  };

  const createRequirementHandler = async () => {
    let preparedValues = formService.prepareValuesToSend(values);
    let result = await createService.createRequirement("ZMENADISTRIBUCNISAZBY_JISTICE_REZERVOVANEHOPRIKONU", preparedValues);
    openResultDialogHandler(result);
  }

  const openResultDialogHandler = (result: PostResponse<InsertRequirementResultDTO>) => {
    const params: RequirementResultDialogParams = {
      response: result,
      successMessage: tLabels("reservedCapacityFormSection.successMessage"),
      errorMessage: tLabels("reservedCapacityFormSection.errorMessage"),
      actionBtn: { title: tLabels("reservedCapacityFormSection.reservedCapacity"), route: getContractDetailRootRoute(values["DESC_TMZMSMLEESMLOUVA"], "reserved-capacity") },
      transitonBtn: null
    };

    openRequirementResultDialog(useOutsideOfRequirements, params, tLabels("reservedCapacityFormSection.changeReservedCapacity"));
  }

  const contractChangeHandler = async (contract: ContractDropdownDTO | null) => {
    if (contract) {
      let generatedYears = formService.generateYears(contract);

      // set state
      setSelectedContract(contract);
      setYears(generatedYears);

      // set select data to form values
      setFormValue("DESC_TMZMSMLEESMLOUVA", contract.contractId);

      // select year
      yearChangeHandler(getSelectedYear(generatedYears), contract);
    }
  }

  const yearChangeHandler = async (year: number, contract?: ContractDropdownDTO) => {
    let generatedMonths = formService.generateMonths(year);
    let selectedMonth = generatedMonths[0].value;

    // fetch data
    let selContract = contract ?? selectedContract; // because state update is async (for call from contractChangeHandler)
    let electricityCapacities = await trackPromise(
      formService.fetchElectricityCapacities(selContract.contractId!, year),
      ReservedCapacityFormSection_PageCard
    );

    // set state
    setMonths(generatedMonths);
    setCapacities(electricityCapacities);

    // set select data to form values
    setFormValue("Year", year);
    setFormValue("Month", selectedMonth);
    formService.setReservedCapacities(electricityCapacities, setFormValue, true);
    formService.setDefaultAnnualCapacity(electricityCapacities, Number(selectedMonth), setFormValue);

    // set capacity validations
    setCapacitiesValidation(electricityCapacities, setValidationRule);

    // after setting values clear errors (if switching contracts/years)
    clearValidationErrors();
  }
  
  const monthChangeHandler = (e: React.ChangeEvent<HTMLSelectElement>) => {
    formService.setDefaultReservedCapacities(capacities, Number(e.target.value), setFormValue);
    setFormValue("Month", e.target.value);    
  }
  
  const monthlyCapacityBlurHandler = (e: React.FocusEvent<HTMLInputElement>, capacity: CapacityElectricityDTO) => {
    validateMonthlyCapacityInput(
      e.target.name,
      e.target.value,
      capacity,
      values["DESC_TMZMSMLEEKAPROCNI"],
      monthlyCapacityError,
      validateFormField,
      setValidationError
    );
  }

  const yarlyCapacityBlurHandler = (e: React.FocusEvent<HTMLInputElement>) => {
    validateAllCapacities(
      values,
      capacities,
      monthlyCapacityError,
      validateFormField,
      setValidationError
    );
  }

  //#endregion handlers

  //#region render
  return (
    <form onSubmit={submitFormHandler}>
      <PageCard 
        title={tLabels("reservedCapacityFormSection.cardTitle")}
        bodyClassName="pt-4 px-4"
        loadingAreaName={ReservedCapacityFormSection_PageCard}
        loadingDelay={0}
      >
        <div className="row no-gutters mx-n2">
          <div className="col-lg-6 px-2">
            <ContractSelect 
              name="DESC_TMZMSMLEESMLOUVA"
              contracts={contracts}
              onChange={contractChangeHandler}
              errors={errors}
              defaultContract={selectedContract}
            />
          </div>
          <div className="col-sm-6 col-lg-3 px-2">
            <SelectInput 
              name="Year"
              options={years} 
              label="Rok"
              onChange={(e) => yearChangeHandler(Number(e.target.value))}
              value={values["Year"]}
              visible={baseFormService.isFilledValues(values, ["DESC_TMZMSMLEESMLOUVA"])}
            />
          </div>
          <div className="col-sm-6 col-lg-3 px-2">
            <SelectInput 
              name="Month"
              options={months} 
              label="Provést změnu od"
              onChange={monthChangeHandler}
              value={values["Month"]}
              visible={baseFormService.isFilledValues(values, ["DESC_TMZMSMLEESMLOUVA"])}
            />
          </div>
          <div className="col-sm-6 px-2">
            <AnnualCapacity 
              bindField={bindField}
              onBlur={yarlyCapacityBlurHandler}
              errors={errors}
              editable={baseFormService.isFilledValues(values, ["DESC_TMZMSMLEEPRIKONPUVOD"])}
            />
          </div>
        </div>
      </PageCard>

      <ReservedCapacitySubForm 
        bindField={bindField}
        capacities={capacities}
        onCapacityInputBlur={monthlyCapacityBlurHandler}
        editable={baseFormService.isFilledValues(values, ["Year"])}
        selectedMonth={values["Month"]}
        errors={errors}
      />

      <RenderWrapper isVisible={() => !useOutsideOfRequirements}>
        <BasicInfoSubFormSection
          bindField={bindField}
          bindUncontrolledField={bindUncontrolledField}
          errors={errors}
          setValidationRule={setValidationRule}
        />
      </RenderWrapper>

      <button type="submit" className="btn btn-primary px-3 py-2">{sendButtonTitle}</button>
    </form>
  )
  //#endregion render
}

export default ReservedCapacityFormSection;
